import { getFullName } from '@/common/utils/person';
import { getCompanyName } from '@/views/Referrals/views/ReferralsList/ReferralsList.helpers';
import type {
  AgreementStatus,
  ReferralAgent,
  ReferralAgentRaw,
  ReferralAgentSummary,
  ReferralAgentSummaryRaw,
  ReferralClient,
  ReferralClientRaw,
  ReferralClientSummary,
  ReferralClientSummaryRaw,
} from './types';
import { ClientType } from './types';

type AgentLike = ReferralAgentRaw | ReferralAgentSummaryRaw;
type ClientLike = ReferralClientRaw | ReferralClientSummaryRaw;

// Overloads
export function augmentAgent(
  agent: ReferralAgentRaw | undefined | null,
): ReferralAgent | undefined;
export function augmentAgent(
  agent: AgentLike | undefined | null,
): ReferralAgentSummary | undefined;
export function augmentAgent(
  agent: AgentLike | undefined | null,
): ReferralAgent | ReferralAgentSummary | undefined {
  if (!agent) return undefined;

  return {
    ...agent,
    order: Number(agent.order),
    displayName: getFullName(agent.firstName, agent.lastName),
    isSideAgent: Boolean(agent.userId),
    companyName: getCompanyName(agent),
  };
}

// Overloads
export function augmentClient(client: ReferralClientRaw): ReferralClient;
export function augmentClient(
  client: ReferralClientRaw | undefined | null,
): ReferralClient | undefined;
export function augmentClient(client: ClientLike): ReferralClientSummary;
export function augmentClient(
  client: ClientLike | undefined | null,
): ReferralClientSummary | undefined;
// Implementation
export function augmentClient(
  client: ClientLike | undefined | null,
): ReferralClient | ReferralClientSummary | undefined {
  if (!client) return undefined;

  const clientTypes = client.clientTypes ?? {
    referralBuyer: false,
    referralSeller: false,
    referralLandlord: false,
    referralTenant: false,
  };

  const clientTypesList: ClientType[] = [];
  if (clientTypes.referralBuyer) clientTypesList.push(ClientType.Buyer);
  if (clientTypes.referralSeller) clientTypesList.push(ClientType.Seller);
  if (clientTypes.referralLandlord) clientTypesList.push(ClientType.Landlord);
  if (clientTypes.referralTenant) clientTypesList.push(ClientType.Tenant);

  return {
    ...client,
    order: Number(client.order),
    clientTypes,
    displayName: getFullName(client.firstName, client.lastName),
    clientTypesList,
  };
}

export function augmentAgreementStatus(
  agreementStatus: AgreementStatus | null,
): AgreementStatus {
  // Map null to 'created' to match the pattern where agreements
  // that haven't been sent yet are in the "created" state
  // (similar to how GraphQL implementation aliased null as NotSent)
  return agreementStatus ?? 'created';
}
